import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { distinctUntilChanged, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { DropdownButtonItem } from '@cv-invision/cv.component-library.ux';
// State
import { Store } from '@ngxs/store';
import { UserContextState } from '../../../../auth/user-context.state';
import { SignalRState } from '../../../../signal-r/signal-r.state';
import { ChangeEventStatusChannelAction } from '../../../../signal-r/action/signal-r.action';
import { ClearModuleState } from '../../../../action/state.action';
// Service
import { CLASS_VALUATION_PROVIDER, UsersService } from '../../service/users.service';
import { SignalRService } from '../../../../signal-r/service/signal-r.service';
import { RoleService } from '../../../../service/role.services';
import { BroadcastService } from '../../../../service/broadcast.service';
import { AppService } from '../../../../service/app.service';
import { AppInformationService } from '../../../../service/app-info.service';
import { UIConfigService } from '../../../../ui-config/service/ui-config.service';
// Model
import { UserContextModel } from '../../../../auth/user-context.model';
import { AUTHACTIONS } from '../../../../model/role.model';
import { EVENT_STATUS } from '../../../../signal-r/model/signal-r.model';
import { GENERAL_EVENT_TYPE, ORDER_EVENT_TYPES } from '../../../../model/order-event-type.model';
// Other
import { DOWNLOADABLE_LINKS } from '../../../download-link/download-link.component';
//utils
import { CookieUtils } from '../../../../utils/cookies.utils';
import { UIConfigBroadcastModel, UIConfigComponent } from '../../../../ui-config/model/ui-config-broadcast.model';
import { UIConfigStyle } from '../../../../ui-config/model/ui-config.model';

export const UPDATE_USER_PROFILE_DATA = 'UPDATE_USER_PROFILE_DATA';

@Component({
	selector: 'cv-invision-header',
	templateUrl: './invision-header.component.html',
	styleUrls: ['../../header.component.scss']
})
export class InvisionHeaderComponent implements OnInit, OnDestroy {
	channels$: Observable<any> = this.store.select(SignalRState.channelEvents);
	user$: Observable<any> = this.store.select(UserContextState.getUserContext);

	@ViewChild('inbox', {static: false}) inbox: ElementRef<any>;
	@ViewChild('inboxBadge', {static: false}) inboxBadge: ElementRef<any>;
	@ViewChild('userProfile', {static: false}) userProfile: ElementRef<any>;
	@ViewChild('initials', {static: false}) initials: ElementRef<any>;

	@Input() toggleUser: any;

	public orderList: any = [];
	public filteredOrderList: any = [];
	public user = {
		fullName: '',
		initials: '',
		userName: '',
		email: '',
		organizationName: '',
		roles: ''
	};
	public downloadableLinks: any = [];
	private atComponentDestroy = new Subject();
	public userModel: UserContextModel;

	appSwitch: boolean;
	headerLogo: string;
	uiConfigBrandLogo: string = null;
	appOptions: Array<any>;
	appDropdown: any = {
		label: '',
		image: ''
	};
	clickListener: any;
	clickUserProfileListener: any;
	module: string;

	constructor(
		private store: Store,
		private router: Router,
		public oidcSecurityService: OidcSecurityService,
		private signalRService: SignalRService,
		private roleService: RoleService,
		private renderer: Renderer2,
		private usersService: UsersService,
		private broadcastService: BroadcastService,
		private appService: AppService,
		private appInfo: AppInformationService,
		private uiConfigService: UIConfigService
	) {
		this.appOptions = [];
		this.onLoadUIConfig();
	}

	ngOnInit(): void {
		// Listen to any change to the Orders
		this.channels$.pipe(
			takeUntil(this.atComponentDestroy)
		).subscribe((channels: any) => {
			if (channels && channels[`orders`]) {
				const channelEvents = channels[`orders`].events;

				const events = channels.orders ? [].concat(channelEvents) : [];
				this.orderList = [...events];

				this.filterOrderDataNotification();
			}
		});

		this.user$
			.pipe(
				take(1),
				tap((user: UserContextModel) => {
					if (user && user.role) {
						this.userModel = user;
						this.appSwitch = this.userCanAccessMultipleApplications(user);
						if (this.appSwitch) {
							this.buildAppSwitch(user);
						} else {
							// at this point it means user has only 1 single permission module access
							const appHeaderData = this.appService.getAppHeader(user.permission);

							if (appHeaderData.logo !== '') {
								this.headerLogo = appHeaderData.logo;
								this.module = appHeaderData.module;
							}
						}

						if (user && user.id) {
							this.getUserData();
						}
					}
				})
			)
			.subscribe();

		this.filterOrderDataNotification();
		this.globalListeners();
		if (this.toggleUser) {
			this.toggleUserProfile();
		}
	}

	/**
	 * Register Listeners for Global use in the app
	 */
	private globalListeners(): void {
		this.broadcastService.on(UPDATE_USER_PROFILE_DATA, () => {
			this.getUserData();
		});
		this.broadcastService.on(DOWNLOADABLE_LINKS, (eventData: any) => {
			this.downloadableLinks.push(eventData.event.link);
		});
	}

	/**
	 * Get and update user data
	 */
	public getUserData(): void {
		this.usersService.getUser(CLASS_VALUATION_PROVIDER, this.userModel.id).subscribe(
			userData => {
				const name = userData ? userData.firstName + ' ' + userData.lastName : '';
				const initials = name
					? name
						.split(' ')
						.map(x => x.charAt(0))
						.join('')
						.substr(0, 2)
						.toUpperCase()
					: 'U';

				this.user = {
					fullName: name,
					initials: initials,
					userName: userData.userName,
					organizationName: userData.organizationName,
					email: userData.email,
					roles: userData.roles.join(' ')
				};
			},
			(errorMsg: any) => {
				console.error(errorMsg);
			}
		);
	}

	/**
	 * Builds up the app options switch based on user permissions.
	 * @param user
	 */
	buildAppSwitch(user: any): void {
		const appSwitcher = this.appService.getAppSwitcherMenu(user, this.appInfo.currentUrl);
		this.appOptions = [];

		this.appOptions = appSwitcher.options;
		// Selected Option
		this.appDropdown = Object.assign({}, appSwitcher.appDropdown);
		// Selected Module
		this.module = this.appDropdown.label;
	}

	/**
	 * Returns true if notification inbox button should be shown.
	 */
	showInboxButton(): boolean {
		return this.appService.getHeaderShowInboxButton(this.module);
	}

	/**
	 * Does user can access multiple application modules ? (based on permissions)
	 * @param user
	 */
	userCanAccessMultipleApplications(user: any): boolean {
		return this.appService.getCanAccessMultipleApps(user.permission);
	}

	/**
	 * Logout user.
	 */
	public logout(): void {
		this.roleService.unAuthSetValues(AUTHACTIONS.LOGOUT);
	}

	/**
	 * Open the Inbox
	 */
	public toggleInbox(): void {
		const {nativeElement} = this.inboxBadge;
		const {nativeElement: el} = this.inbox;
		if (this.inboxBadge && el.style.display !== 'block') {
			const top = nativeElement.offsetTop + nativeElement.offsetHeight;
			this.renderer.setStyle(el, 'top', `${top + 2}px`);
			this.renderer.setStyle(el, 'display', 'block');
			this.registerInboxClickListener();
		} else {
			this.renderer.setStyle(el, 'display', 'none');
			if (this.clickListener) {
				this.clickListener();
			}
		}
	}

	/**
	 * Open the User Profile
	 */
	public toggleUserProfile(): void {
		const {nativeElement} = this.initials;
		const {nativeElement: el} = this.userProfile;
		if (el.style.display !== 'block') {
			const top = nativeElement.offsetTop + nativeElement.offsetHeight;
			this.renderer.setStyle(el, 'top', `${top + 15}px`);
			this.renderer.setStyle(el, 'display', 'block');
			this.registerUserProfileClickListener();
		} else {
			this.renderer.setStyle(el, 'display', 'none');
			if (this.clickUserProfileListener) {
				this.clickUserProfileListener();
			}
		}
	}

	/**
	 * Register a document:click event listener ONLY when inbox opens up in order
	 * to close the inbox by clicking outside of it. This event is destroyed whenever the
	 * inbox is closed either by toggle button or outside click.
	 */
	registerInboxClickListener(): void {
		setTimeout(() => {
			this.clickListener = this.renderer.listen('document', 'click', event => {
				const targetElement = event.target;
				const el = this.inbox.nativeElement;
				const clickedOutside = !el.contains(targetElement);
				if (clickedOutside) {
					this.renderer.setStyle(el, 'display', 'none');
					this.clickListener();
				}
			});
		}, 100);
	}

	/**
	 * Register a document:click event listener ONLY when inbox opens up in order
	 * to close the inbox by clicking outside of it. This event is destroyed whenever the
	 * inbox is closed either by toggle button or outside click.
	 */
	registerUserProfileClickListener(): void {
		setTimeout(() => {
			this.clickUserProfileListener = this.renderer.listen('document', 'click', event => {
				const targetElement = event.target;
				const el = this.userProfile.nativeElement;
				const clickedOutside = !el.contains(targetElement);
				if (clickedOutside) {
					this.renderer.setStyle(el, 'display', 'none');
					this.clickUserProfileListener();
				}
			});
		}, 100);
	}

	/**
	 * Get How many new events are in place
	 */
	public getNewEventsCount(): number {
		const newEvents: Array<any> = this.getNewEvents();
		if (newEvents) {
			return newEvents.length;
		}
		return null;
	}

	/**
	 * Get new events that are in place.
	 */
	getNewEvents(): Array<any> {
		return this.filteredOrderList.filter((order: any) => {
			return order.status === EVENT_STATUS.NEW && ORDER_EVENT_TYPES.includes(order.event);
		});
	}

	/**
	 * Change the status of all the events
	 */
	public markAllAsRead(): void {
		this.store.dispatch(new ChangeEventStatusChannelAction('orders', EVENT_STATUS.OLD));
		const events = this.getNewEvents();
		const orderChannels = new Set<string>();
		events.forEach((order: any) => {
			if (order.data && order.data.orderId && !orderChannels.has(`orders:${order.data.orderId}`)) {
				const orderChannel = `orders:${order.data.orderId}`;
				orderChannels.add(orderChannel);
				this.store.dispatch(new ChangeEventStatusChannelAction(orderChannel, EVENT_STATUS.OLD));
			}
		});
	}

	/**
	 * filter notification depend on the module
	 */
	filterOrderDataNotification(): void {
		if (this.orderList.length && this.module) {
			const ignoreFormTypeApps = this.appService.getIgnoreFormTypeApps();

			if (ignoreFormTypeApps.some(app => this.module.includes(app))) {
				this.filteredOrderList = this.orderList.filter((r: any) => !r.data.formType);
			} else {
				this.filteredOrderList = this.orderList.filter((r: any) => r.data.formType);
			}
		}
	}

	/**
	 * Change the Current Submodule App
	 * Clears also previous state
	 */
	goToApp($event: { item: DropdownButtonItem }): void {
		// if change the module reset previous module values
		if (!this.appInfo.currentUrl.includes($event.item.label)) {
			this.appDropdown = Object.assign({}, $event.item);
			this.module = $event.item.label;
			// reset all orders channels
			this.ordersUnsubscribe();
			this.filterOrderDataNotification();
			this.signalRService.stopConnection();
			this.roleService.setSignalRbyRole($event.item.label);
			this.store.dispatch(new ClearModuleState(this.appInfo.currentmodule));
			this.router.navigate([`/${$event.item.label}`]).catch(e => console.warn(e));
		}
	}

	/**
	 * unsubribe whether there are orders subcribe to signalR
	 */
	ordersUnsubscribe(): void {
		const channels = this.store.selectSnapshot(SignalRState.channelEvents);
		Object.keys(channels).forEach(ch => {
			// just check for real orders no order channel
			if (ch !== 'orders') {
				this.signalRService.unregisterChannel(ch);
				this.signalRService
					.invokeHubPublic(GENERAL_EVENT_TYPE.UNSUBSCRIBE, ch)
					.then(() => {
						//
					})
					.catch(() => {
						// Swallow
					});
			}
		});
	}

	/**
	 * Erasing cookies when logout
	 */
	eraseCookies(): void {
		CookieUtils.delete('CloudFront-Policy');
		CookieUtils.delete('CloudFront-Signature');
		CookieUtils.delete('CloudFront-Key-Pair-Id');
	}

	/**
	 * Get Header Configuration
	 * Reason to get it inside and not outside is because there can be logic or listeners
	 * as part of the Header applying an ngIF could break some functionality
	 * @private
	 */
	private onLoadUIConfig(): void {
		// If something changes again, open only for this component
		this.uiConfigService.onUiConfigModelChange
			.pipe(
				distinctUntilChanged(),
				filter((uiConfigBroadcastModel: UIConfigBroadcastModel) =>
					uiConfigBroadcastModel.component === UIConfigComponent.LOGO
				)
			)
			.subscribe((uiConfigBroadcastModel: UIConfigBroadcastModel) => {
				this.uiConfigBrandLogo = this.uiConfigService.getComponentStyle(UIConfigComponent.LOGO);
			});

		this.uiConfigBrandLogo = this.uiConfigService.getComponentStyle(UIConfigComponent.LOGO);
	}

	/**
	 * Close all when the element is destroyed
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
		this.eraseCookies();
	}
}
